/* SSQuiz v2 — MenGoToMars-style auto-advance quiz funnel
 * Vanilla JS. No framework. Works via window.SSQ_CONFIG + window.SSQuiz.init().
 * Heterogeneous screens: intro | question | interstitial | offer
 * Contract: see quiz-config.schema.json
 */
(function (global) {
  "use strict";

  var STORAGE_PREFIX = "ssq:";
  var ADVANCE_DELAY = 260;

  // ────────────────────────────────────────────────────────────────
  function makeStorage(slug) {
    var key = STORAGE_PREFIX + slug;
    var memory = null;
    var useSession = false;
    try {
      sessionStorage.setItem(STORAGE_PREFIX + "__probe", "1");
      sessionStorage.removeItem(STORAGE_PREFIX + "__probe");
      useSession = true;
    } catch (e) { useSession = false; }
    return {
      get: function () {
        if (useSession) {
          var raw = sessionStorage.getItem(key);
          return raw ? JSON.parse(raw) : null;
        }
        return memory;
      },
      set: function (s) {
        if (useSession) sessionStorage.setItem(key, JSON.stringify(s));
        else memory = s;
      },
      clear: function () {
        if (useSession) sessionStorage.removeItem(key);
        else memory = null;
      }
    };
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) for (var k in attrs) {
      if (!Object.prototype.hasOwnProperty.call(attrs, k)) continue;
      var v = attrs[k];
      if (v == null) continue;
      if (k === "class") node.className = v;
      else if (k === "html") node.innerHTML = v;
      else if (k.indexOf("on") === 0 && typeof v === "function") {
        node.addEventListener(k.slice(2).toLowerCase(), v);
      } else node.setAttribute(k, v);
    }
    if (children) (Array.isArray(children) ? children : [children]).forEach(function (c) {
      if (c == null) return;
      node.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
    });
    return node;
  }

  function uuid() {
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }

  function withUtm(url, slug, personaKey) {
    if (!url) return "#";
    var fragIdx = url.indexOf("#");
    var base = fragIdx > -1 ? url.slice(0, fragIdx) : url;
    var frag = fragIdx > -1 ? url.slice(fragIdx) : "";
    var sep = base.indexOf("?") > -1 ? "&" : "?";
    var params = "utm_source=quiz&utm_medium=" + encodeURIComponent(slug) +
                 "&utm_content=" + encodeURIComponent(personaKey || "default");
    return base + sep + params + frag;
  }

  // ────────────────────────────────────────────────────────────────
  // Screen filtering + question counting
  // ────────────────────────────────────────────────────────────────
  function shouldShow(screen, answers) {
    if (!screen.showIf) return true;
    for (var qid in screen.showIf) {
      var wanted = screen.showIf[qid];
      var got = answers[qid];
      if (!got) return false;
      var gotArr = Array.isArray(got) ? got : [got];
      if (!gotArr.some(function (v) { return wanted.indexOf(v) !== -1; })) return false;
    }
    return true;
  }

  function visibleScreens(config, answers) {
    return (config.screens || []).filter(function (s) { return shouldShow(s, answers); });
  }

  function questionIndex(screens, idx) {
    // 1-based position among question-type screens up to idx
    var n = 0;
    for (var i = 0; i <= idx && i < screens.length; i++) {
      if (screens[i].type === "question" || screens[i].type === "intro") n++;
    }
    return n;
  }

  function totalQuestions(screens) {
    return screens.filter(function (s) { return s.type === "question" || s.type === "intro"; }).length;
  }

  // ────────────────────────────────────────────────────────────────
  // Scoring + persona resolution
  // ────────────────────────────────────────────────────────────────
  function scoreAnswers(config, answers) {
    var scores = {};
    (config.screens || []).forEach(function (s) {
      if (s.type !== "question" && s.type !== "intro") return;
      var a = answers[s.id];
      if (!a) return;
      var selected = Array.isArray(a) ? a : [a];
      selected.forEach(function (optId) {
        var opt = (s.options || []).find(function (o) { return o.id === optId; });
        if (!opt || !opt.tags) return;
        for (var tag in opt.tags) scores[tag] = (scores[tag] || 0) + opt.tags[tag];
      });
    });
    return scores;
  }

  function resolvePersona(config, scores) {
    var best = null;
    var bestScore = -Infinity;
    for (var key in config.personas) {
      var persona = config.personas[key];
      var threshold = persona.tagThreshold || {};
      var thresholdKeys = Object.keys(threshold);
      var passes = true;
      var combined = 0;
      for (var i = 0; i < thresholdKeys.length; i++) {
        var tag = thresholdKeys[i];
        var have = scores[tag] || 0;
        if (have < threshold[tag]) { passes = false; break; }
        combined += have;
      }
      if (!passes) continue;
      if (thresholdKeys.length === 0) {
        for (var t in scores) combined += scores[t] || 0;
      }
      if (combined > bestScore) { bestScore = combined; best = key; }
    }
    if (!best) best = config.defaultPersona || Object.keys(config.personas)[0];
    return best;
  }

  // ────────────────────────────────────────────────────────────────
  // Tracking — fans out to Klaviyo + Meta Pixel + dataLayer + PostHog
  // ────────────────────────────────────────────────────────────────
  function snakeCase(s) {
    return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
  }

  function klaviyoEvent(config, metricName, payload, opts) {
    var kl = config.klaviyo;
    if (!kl || !kl.company_id) return Promise.resolve();
    var body = {
      data: {
        type: "event",
        attributes: {
          properties: payload.properties || {},
          metric: { data: { type: "metric", attributes: { name: metricName } } },
          profile: { data: { type: "profile", attributes: payload.profile } }
        }
      }
    };
    var fetchOpts = {
      method: "POST",
      headers: { "Content-Type": "application/json", "revision": "2024-10-15" },
      body: JSON.stringify(body)
    };
    if (opts && opts.keepalive) fetchOpts.keepalive = true;
    return fetch("https://a.klaviyo.com/client/events/?company_id=" + encodeURIComponent(kl.company_id), fetchOpts)
      .catch(function (err) { console.warn("[SSQ] Klaviyo event failed", metricName, err); });
  }

  function metaPixelEvent(eventName, props, isStandard) {
    if (typeof window === "undefined" || typeof window.fbq !== "function") return;
    try {
      window.fbq(isStandard ? "track" : "trackCustom", eventName, props || {});
    } catch (e) { /* swallow */ }
  }

  function dataLayerPush(eventName, props) {
    if (typeof window === "undefined" || !Array.isArray(window.dataLayer)) {
      if (typeof window !== "undefined") window.dataLayer = window.dataLayer || [];
      else return;
    }
    var entry = { event: snakeCase(eventName) };
    for (var k in props) entry[k] = props[k];
    window.dataLayer.push(entry);
  }

  function trackEvent(state, name, extraProps, opts) {
    var base = {
      session_id: state.sessionId,
      quiz_slug: state.config.slug,
      brand: state.config.brand,
      step_index: state.index,
      total_steps: state.totalScreens || 0,
      persona_tag: state.persona || null,
      timestamp_iso: new Date().toISOString()
    };
    var props = Object.assign({}, base, extraProps || {});
    var profileAttrs = state.email
      ? { email: state.email, anonymous_id: state.sessionId }
      : { anonymous_id: state.sessionId };

    // 1. Klaviyo — only after email capture, otherwise we create anonymous-only
    //    profiles that bloat Klaviyo's billable Active count and cannot be
    //    deleted via API. PostHog captures every event regardless for analytics.
    if (state.email) {
      klaviyoEvent(state.config, name, { properties: props, profile: profileAttrs }, opts);
    }

    // 2. Meta Pixel (if loaded). Map a few names to standard events for ad-platform optimization.
    var metaName = name.replace(/\s+/g, "");
    var standardMap = { "QuizCompleted": null, "QuizStarted": null, "EmailCaptured": "Lead", "QuizCTAClicked": "InitiateCheckout" };
    if (Object.prototype.hasOwnProperty.call(standardMap, metaName) && standardMap[metaName]) {
      metaPixelEvent(standardMap[metaName], props, true);
    }
    metaPixelEvent(metaName, props, false); // always also fire as custom

    // 3. dataLayer (GTM/GA4)
    dataLayerPush(name, props);

    // 4. PostHog (via PAI shared track.js — preferred) or fallback to direct posthog.capture()
    if (typeof window !== "undefined") {
      if (window.PAI && typeof window.PAI.track === "function") {
        window.PAI.track(name, props, opts);
      } else if (typeof window.posthog !== "undefined" && typeof window.posthog.capture === "function") {
        window.posthog.capture(name, props);
      }
    }

    // 5. Debug log for devs
    if (typeof console !== "undefined" && console.debug) {
      console.debug("[SSQ track]", name, props);
    }
  }

  function klaviyoSubscribe(config, email) {
    var kl = config.klaviyo;
    if (!kl || !kl.company_id || !kl.list_id || !email) return Promise.resolve();
    var body = {
      data: {
        type: "subscription",
        attributes: {
          custom_source: (config.slug || "quiz") + " — Quiz Funnel",
          profile: { data: { type: "profile", attributes: {
            email: email,
            subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } }
          } } }
        },
        relationships: { list: { data: { type: "list", id: kl.list_id } } }
      }
    };
    return fetch("https://a.klaviyo.com/client/subscriptions/?company_id=" + encodeURIComponent(kl.company_id), {
      method: "POST",
      headers: { "Content-Type": "application/json", "revision": "2024-10-15" },
      body: JSON.stringify(body)
    }).catch(function (err) { console.warn("[SSQ] Klaviyo subscribe failed", err); });
  }

  // ────────────────────────────────────────────────────────────────
  // Rendering primitives
  // ────────────────────────────────────────────────────────────────
  function renderProgress(state, screens) {
    var total = totalQuestions(screens);
    var current = questionIndex(screens, state.index);
    if (screens[state.index] && screens[state.index].type !== "question" && screens[state.index].type !== "intro") {
      // interstitial/offer — freeze progress at last question position
      current = Math.min(current, total);
    }
    var pct = Math.max(4, Math.round((current / total) * 100));
    return el("div", { class: "ssq-progress-rail" }, [
      el("div", { class: "ssq-progress-fill", style: "width:" + pct + "%" })
    ]);
  }

  function renderOption(state, screen, opt, autoAdvance) {
    var answers = state.answers[screen.id];
    var selected = screen.kind === "multi"
      ? Array.isArray(answers) && answers.indexOf(opt.id) !== -1
      : answers === opt.id;

    var card = el("button", {
      type: "button",
      class: "ssq-option" + (selected ? " ssq-selected" : ""),
      role: screen.kind === "multi" ? "checkbox" : "radio",
      "aria-checked": selected ? "true" : "false",
      "data-opt": opt.id
    }, [
      el("span", { class: "ssq-option-indicator" }),
      el("span", { class: "ssq-option-body" }, [
        el("span", { class: "ssq-option-label" }, opt.label),
        opt.description ? el("span", { class: "ssq-option-desc" }, opt.description) : null
      ])
    ]);

    card.addEventListener("click", function () {
      haptic(8);
      if (screen.kind === "multi") {
        var arr = Array.isArray(state.answers[screen.id]) ? state.answers[screen.id].slice() : [];
        var i = arr.indexOf(opt.id);
        if (i === -1) arr.push(opt.id); else arr.splice(i, 1);
        state.answers[screen.id] = arr;
        state.persist();
        state.render();
        // Don't fire Question Answered for multi until user hits Continue (committed answer set).
      } else {
        state.answers[screen.id] = opt.id;
        state.persist();
        trackEvent(state, "Quiz Question Answered", {
          question_id: screen.id,
          question_prompt: screen.prompt,
          answer_id: opt.id,
          answer_label: opt.label,
          kind: "single",
          scores_snapshot: scoreAnswers(state.config, state.answers)
        });
        // Single-select: advance immediately. No intermediate re-render, no
        // ack flash, no delay. Tap → transport.
        if (autoAdvance) {
          advance(state);
        } else {
          state.render();
        }
      }
    });
    return card;
  }

  // ────────────────────────────────────────────────────────────────
  // Dopamine — haptic, confetti, completion chime.
  // All fire-and-forget. Never block navigation.
  // ────────────────────────────────────────────────────────────────
  function haptic(pattern) {
    // Android Chrome supports arrays (sequence of ms on/off). iOS Safari
    // does not support navigator.vibrate — silent no-op there.
    try { if (navigator.vibrate) navigator.vibrate(pattern || 10); } catch (e) {}
  }

  function fireConfetti(root) {
    // Self-contained, zero-dep confetti. ~80 particles, 1.4s.
    if (!root) return;
    var canvas = document.createElement("canvas");
    canvas.className = "ssq-confetti";
    canvas.width = root.clientWidth;
    canvas.height = Math.min(root.clientHeight, 480);
    var ctx = canvas.getContext("2d");
    root.appendChild(canvas);

    var colors = ["#FF5600", "#FFB380", "#0F1419", "#F6F3ED", "#FFD9B8"];
    var parts = [];
    var W = canvas.width;
    var H = canvas.height;
    for (var i = 0; i < 80; i++) {
      parts.push({
        x: W / 2 + (Math.random() - 0.5) * W * 0.3,
        y: H * 0.4,
        vx: (Math.random() - 0.5) * 8,
        vy: Math.random() * -8 - 4,
        size: 4 + Math.random() * 5,
        color: colors[Math.floor(Math.random() * colors.length)],
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 0.3
      });
    }

    var start = Date.now();
    var duration = 1400;

    function tick() {
      var t = Date.now() - start;
      ctx.clearRect(0, 0, W, H);
      parts.forEach(function (p) {
        p.vy += 0.25; // gravity
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.globalAlpha = Math.max(0, 1 - t / duration);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.4);
        ctx.restore();
      });
      if (t < duration) {
        requestAnimationFrame(tick);
      } else {
        if (canvas.parentElement) canvas.parentElement.removeChild(canvas);
      }
    }
    requestAnimationFrame(tick);
  }

  // Audio removed — too much autoplay-policy + silent-switch surface area
  // for the dopamine gain. Haptic + confetti + analyzing-pause carry the load.

  function showAck(state, ackText, done) {
    var root = state.root;
    if (!root) { done(); return; }
    // Remove any existing ack (idempotent if user rage-clicks)
    var existing = root.querySelector(".ssq-ack-overlay");
    if (existing) existing.parentElement.removeChild(existing);
    var overlay = el("div", { class: "ssq-ack-overlay", role: "status", "aria-live": "polite" }, [
      el("div", { class: "ssq-ack-bubble" }, [
        el("span", { class: "ssq-ack-icon", "aria-hidden": "true" }, "◆"),
        el("span", { class: "ssq-ack-text" }, ackText)
      ])
    ]);
    root.appendChild(overlay);
    setTimeout(function () {
      try {
        if (overlay.parentElement) overlay.parentElement.removeChild(overlay);
      } catch (e) {}
      done();
    }, 1000);
  }

  function advance(state) {
    var screens = visibleScreens(state.config, state.answers);
    if (state.index + 1 >= screens.length) {
      // Reached end — show offer (already should be an offer screen)
      state.index = screens.length - 1;
    } else {
      state.index += 1;
    }
    state.persist();
    state.render();
  }

  function renderTestimonial(t) {
    if (!t) return null;
    return el("figure", { class: "ssq-testimonial" }, [
      el("blockquote", {}, "\u201C" + t.quote + "\u201D"),
      el("figcaption", {}, (t.attribution || "") + (t.verified ? " • Verified Customer" : ""))
    ]);
  }

  function renderQuestionBody(state, screen, includeStartHero) {
    var answers = state.answers[screen.id];
    var answered = screen.kind === "multi"
      ? Array.isArray(answers) && answers.length > 0
      : !!answers;
    var autoAdvance = screen.kind !== "multi";

    var pieces = [];
    // Intro screens: headline → question → options → social proof underneath.
    // Subheadline is intentionally skipped on intro — we want the user hitting a
    // micro-commitment before reading another paragraph of copy.
    if (includeStartHero) {
      pieces.push(el("h1", { class: "ssq-headline" }, state.config.headline));
    }
    pieces.push(el("h2", { class: "ssq-q-prompt" }, screen.prompt));
    if (screen.helper) pieces.push(el("p", { class: "ssq-q-helper" }, screen.helper));
    pieces.push(el("div", { class: "ssq-options ssq-options-" + (screen.kind || "single") },
      screen.options.map(function (o) { return renderOption(state, screen, o, autoAdvance); })
    ));
    // PS payoff promise sits directly under the first micro-commitment on intro —
    // rewards the user for staying through the whole flow.
    if (includeStartHero && state.config.introPS) {
      pieces.push(el("p", { class: "ssq-intro-ps" }, [
        el("span", { class: "ssq-intro-ps-tag" }, "PS — "),
        state.config.introPS
      ]));
    }
    // Social proof goes BELOW the micro-commitment on intro, never between.
    if (includeStartHero && state.config.introTestimonial) {
      pieces.push(renderTestimonial(state.config.introTestimonial));
    }

    // Nav: Back always (except first), Next only on multi
    var nav = el("div", { class: "ssq-nav" }, [
      state.index > 0
        ? el("button", {
            type: "button", class: "ssq-back",
            onclick: function () { state.index = Math.max(0, state.index - 1); state.persist(); state.render(); }
          }, "\u2190 Back")
        : el("span", { class: "ssq-back-placeholder" }),
      screen.kind === "multi"
        ? el("button", {
            type: "button",
            class: "ssq-next" + (answered ? "" : " ssq-next-disabled"),
            disabled: answered ? null : "disabled",
            onclick: function () {
              if (!answered) return;
              haptic([10, 40, 10]);
              trackEvent(state, "Quiz Question Answered", {
                question_id: screen.id,
                question_prompt: screen.prompt,
                answer_ids: state.answers[screen.id],
                kind: "multi",
                scores_snapshot: scoreAnswers(state.config, state.answers)
              });
              advance(state);
            }
          }, "Continue \u2192")
        : el("span", { class: "ssq-back-placeholder" })
    ]);
    pieces.push(nav);

    return el("section", { class: "ssq-card" + (includeStartHero ? " ssq-card-intro" : "") }, pieces);
  }

  function renderAnalyzing(state, screen) {
    // The slot-machine pause. Rotating copy lines with a progress bar filling
    // 0 → 100% over `duration` ms, then auto-advance. This is the 10-20%
    // perceived-value lever — the wait IS the dopamine.
    var duration = screen.duration || 3000;
    var lines = screen.lines && screen.lines.length ? screen.lines : [
      "Mapping your stack against 697 Reddit threads...",
      "Matching your pain pattern...",
      "Cross-referencing 134 verbatim complaints...",
      "Locking your protocol..."
    ];
    var perLine = Math.floor(duration / lines.length);

    var linesWrap = el("div", { class: "ssq-analyzing-lines" },
      lines.map(function (l, i) {
        return el("div", {
          class: "ssq-analyzing-line",
          "data-line": String(i),
          style: "animation-delay: " + (i * perLine) + "ms"
        }, [
          el("span", { class: "ssq-analyzing-check" }, "✓"),
          el("span", { class: "ssq-analyzing-line-text" }, l)
        ]);
      })
    );

    var fillBar = el("div", { class: "ssq-analyzing-bar" }, [
      el("div", {
        class: "ssq-analyzing-bar-fill",
        style: "animation-duration: " + duration + "ms"
      })
    ]);

    var card = el("section", { class: "ssq-card ssq-analyzing" }, [
      el("div", { class: "ssq-analyzing-pulse", "aria-hidden": "true" }),
      el("h2", { class: "ssq-analyzing-headline" }, screen.headline || "Analyzing your stack..."),
      screen.subline ? el("p", { class: "ssq-analyzing-subline" }, screen.subline) : null,
      linesWrap,
      fillBar,
      renderTestimonial(screen.testimonial)
    ]);

    // Auto-advance at the end of the build.
    setTimeout(function () {
      // Only advance if user is still on this screen (didn't navigate back).
      var screens = visibleScreens(state.config, state.answers);
      if (screens[state.index] && screens[state.index].id === screen.id) {
        advance(state);
      }
    }, duration);

    return card;
  }

  function renderInterstitial(state, screen) {
    var onContinue = function () { advance(state); };
    return el("section", { class: "ssq-card ssq-interstitial" }, [
      screen.tag ? el("div", { class: "ssq-result-tag" }, screen.tag) : null,
      el("h2", { class: "ssq-interstitial-headline" }, screen.headline),
      screen.body ? el("p", { class: "ssq-interstitial-body" }, screen.body) : null,
      renderTestimonial(screen.testimonial),
      el("button", {
        type: "button", class: "ssq-next ssq-interstitial-cta",
        onclick: onContinue
      }, screen.cta || "Continue \u2192")
    ]);
  }

  function buildCartUrl(template, variantId) {
    if (!template) return "#";
    // {{variant_id}} or {variant_id} placeholder gets replaced; otherwise inject if /cart/ path
    if (template.indexOf("{{variant_id}}") > -1 || template.indexOf("{variant_id}") > -1) {
      return template.replace(/\{\{?variant_id\}?\}/g, variantId);
    }
    // Fallback: replace whatever ID is between /cart/ and :
    return template.replace(/\/cart\/(\d+):/, "/cart/" + variantId + ":");
  }

  function renderFlavourPicker(state, offer, onPick) {
    var variants = offer.variants || [];
    if (!variants.length) return null;
    var selectedId = state.selectedVariantId || (variants.find(function (v) { return v.default; }) || variants[0]).id;
    state.selectedVariantId = selectedId;

    return el("div", { class: "ssq-flavour-picker" }, variants.map(function (v) {
      var isSelected = String(v.id) === String(selectedId);
      var btn = el("button", {
        type: "button",
        class: "ssq-flavour" + (isSelected ? " ssq-flavour-selected" : ""),
        "aria-pressed": isSelected ? "true" : "false",
        "data-variant": v.id
      }, [
        v.image ? el("img", { class: "ssq-flavour-img", src: v.image, alt: v.label, loading: "lazy" }) : null,
        el("span", { class: "ssq-flavour-label" }, v.label)
      ]);
      btn.addEventListener("click", function () {
        state.selectedVariantId = v.id;
        state.persist();
        trackEvent(state, "Quiz Variant Selected", {
          variant_id: v.id,
          variant_label: v.label
        });
        onPick(v);
      });
      return btn;
    }));
  }

  function renderBenefits(benefits) {
    if (!benefits || !benefits.length) return null;
    return el("div", { class: "ssq-benefits" }, [
      el("h3", { class: "ssq-benefits-headline" }, "Why UNHINGE wins your protocol"),
      el("ul", { class: "ssq-benefits-list" }, benefits.map(function (b) {
        return el("li", { class: "ssq-benefit" }, [
          el("span", { class: "ssq-benefit-marker", "aria-hidden": "true" }, "◆"),
          el("div", { class: "ssq-benefit-body" }, [
            el("span", { class: "ssq-benefit-title" }, b.title),
            b.body ? el("span", { class: "ssq-benefit-desc" }, b.body) : null
          ])
        ]);
      }))
    ]);
  }

  // Answer-ID → "here's how we fix THAT" map for reveal-screen personalization.
  // User's actual taps get mirrored back with the specific mechanism in the
  // formula that addresses each.
  var FIX_FOR_ANSWER = {
    q2_jitters:        { pain: "Hard crash, sweats, heart-race.",                fix: "200mg measured caffeine + L-Tyrosine buffer. No 300mg+ stim, no crash cliff." },
    q2_flat:           { pain: "Built tolerance. Nothing kicks anymore.",        fix: "Clinical doses (Alpha-GPC 400mg, Bacopa 300mg) bypass the stim loop." },
    q2_pump_no_focus:  { pain: "Body fires up. Brain's in the carpark.",         fix: "Nootropic stack on top of the pump — focus lands 20 min in." },
    q2_tried_everything: { pain: "Tried 10-20 different tubs. None delivered.", fix: "Every mg on the label. No blends. The tub built to your standards." },
    q3_prop_blend:     { pain: "'Proprietary Blend' hiding the doses.",          fix: "Zero proprietary blends. Every ingredient listed with exact mg." },
    q3_kitchen_sink:   { pain: "Kitchen-sink ingredient lists, no doses.",       fix: "16 ingredients, each clinically-dosed. No filler." },
    q3_on_paper:       { pain: "Looks stacked on paper, empty on the mg.",      fix: "6g Citrulline, 3.2g Beta-Alanine, 200mg Caffeine, 400mg Alpha-GPC." },
    q3_caffeine_no_mg: { pain: "'Caffeine anhydrous' with no milligrams.",       fix: "Caffeine listed as 200mg, front of label. No legal fudges." },
    q4_locked_in:      { pain: "You want locked-in focus, rep count in head.",  fix: "Alpha-GPC 400mg + Bacopa + Huperzine. Focus in 20 min, no drift." },
    q4_pump_holds:     { pain: "You want a pump that holds past set two.",      fix: "6g Citrulline + 3.2g Beta-Alanine. Pump holds through heavy sets." },
    q4_no_fog:         { pain: "No brain fog back-half of the session.",        fix: "Lion's Mane 500mg + Bacopa. Fog doesn't return at minute 40." },
    q4_clean_curve:    { pain: "Clean stim curve — no cliff at minute 40.",     fix: "L-Tyrosine buffer smooths the curve. No cliff." },
    q5_coffee:         { pain: "Extra coffee or energy drinks on top.",         fix: "200mg caffeine — your morning + pre-workout in one scoop." },
    q5_nootropics:     { pain: "Standalone nootropics on top.",                  fix: "Already in the scoop. Drop the separate bottles." },
    q5_ashwa_rhodiola: { pain: "Ashwagandha/Rhodiola on top.",                   fix: "L-Tyrosine handles the stress-buffering in-formula." },
    q5_just_scoop:     { pain: "You keep it minimal — just the scoop.",          fix: "Perfect fit. Single-serve, complete stack, no pill layering." },
    q5_trt_sarms:      { pain: "Optimised bloodwork (TRT / PCT).",               fix: "Zero hormonal or metabolic conflict with exogenous protocols." },
    q6_transparency:   { pain: "Every ingredient dosed + disclosed.",            fix: "16 ingredients, exact mg + rationale on the label." },
    q6_nootropic_stack: { pain: "A real nootropic stack, not pixie-dust.",       fix: "Alpha-GPC 400mg, Lion's Mane 500mg, Bacopa 300mg, Huperzine A." },
    q6_clean_curve:    { pain: "A clean stim curve, no crash.",                  fix: "Caffeine + L-Tyrosine. Ramps clean, holds, eases off." },
    q6_pump_doses:     { pain: "6g citrulline or it's not a pump supp.",         fix: "L-Citrulline 6g. Beta-Alanine 3.2g. On the label, not in a 'matrix'." }
  };

  function buildRevealFixRows(state) {
    var rows = [];
    (state.config.screens || []).forEach(function (s) {
      if (s.type !== "question" && s.type !== "intro") return;
      var a = state.answers[s.id];
      if (!a) return;
      var ids = Array.isArray(a) ? a : [a];
      ids.forEach(function (id) {
        var f = FIX_FOR_ANSWER[id];
        if (f) rows.push(f);
      });
    });
    // Cap at 5 so the screen stays scannable on mobile.
    return rows.slice(0, 5);
  }

  function renderResultReveal(state, screen) {
    var scores = scoreAnswers(state.config, state.answers);
    var key = resolvePersona(state.config, scores);
    state.persona = key;
    var persona = state.config.personas[key] || {};
    var reveal = (persona.reveal) || (screen.reveal) || {};
    if (!state.firedCompletion) fireCompletion(state);

    // Defaults if persona didn't override
    var label = persona.label || reveal.label || "Your protocol";
    var diagnosis = reveal.diagnosis || persona.body || "Here's what your answers actually map to.";
    var benefits = reveal.benefits || [];
    var ctaLabel = reveal.ctaLabel || screen.ctaLabel || "Claim my protocol \u2192";
    var ctaUrlBase = reveal.ctaUrl || screen.ctaUrl || "/pages/unhinge-starter-kit";
    var defaultVariant = (state.config.defaultVariantId) || "62711003119775";

    // Pack the user's actual answer IDs into the URL so the destination LP
    // can render answer-specific "here's how we fix THAT" copy — much sharper
    // than a bucket persona alone.
    var answerIds = [];
    (state.config.screens || []).forEach(function (s) {
      if (s.type !== "question" && s.type !== "intro") return;
      var a = state.answers[s.id];
      if (Array.isArray(a)) answerIds = answerIds.concat(a);
      else if (a) answerIds.push(a);
    });
    var url = ctaUrlBase
      + (ctaUrlBase.indexOf("?") > -1 ? "&" : "?")
      + "persona=" + encodeURIComponent(key)
      + "&variant=" + encodeURIComponent(state.selectedVariantId || defaultVariant)
      + "&answers=" + encodeURIComponent(answerIds.join(","));
    url = withUtm(url, state.config.slug, key);

    var benefitNodes = benefits.map(function (b) {
      return el("li", { class: "ssq-reveal-benefit" }, [
        el("span", { class: "ssq-benefit-marker", "aria-hidden": "true" }, "◆"),
        el("span", { class: "ssq-reveal-benefit-text" }, b)
      ]);
    });

    // Timeline block — "first scoop / day 3 / week 2" outcome promise.
    var timeline = reveal.timeline || [];
    var timelineNode = timeline.length ? el("div", { class: "ssq-timeline" }, [
      el("h3", { class: "ssq-timeline-headline" }, reveal.timelineHeadline || "What changes"),
      el("ol", { class: "ssq-timeline-list" }, timeline.map(function (phase, i) {
        return el("li", { class: "ssq-timeline-phase" }, [
          el("div", { class: "ssq-timeline-marker" }, [
            el("span", { class: "ssq-timeline-num" }, String(i + 1)),
            i < timeline.length - 1 ? el("span", { class: "ssq-timeline-connector" }) : null
          ]),
          el("div", { class: "ssq-timeline-body" }, [
            el("span", { class: "ssq-timeline-when" }, phase.when),
            el("p", { class: "ssq-timeline-what" }, phase.what)
          ])
        ]);
      }))
    ]) : null;

    var link = el("a", {
      class: "ssq-next ssq-offer-cta ssq-reveal-cta",
      href: url,
      target: "_self"
    }, ctaLabel);
    link.addEventListener("click", function () {
      trackEvent(state, "Quiz CTA Clicked", {
        destination: "starter_kit_lp",
        persona_key: key,
        href: url,
        variant_id: state.selectedVariantId || defaultVariant
      }, { keepalive: true });
    });

    // Answer-specific fix rows — each thing the user flagged mirrored back
    // with the exact mechanism in UNHINGE that addresses it.
    var fixRows = buildRevealFixRows(state);
    var fixNode = fixRows.length ? el("div", { class: "ssq-reveal-fixes" }, [
      el("h3", { class: "ssq-reveal-fixes-headline" }, "Based on what you told me"),
      el("ul", { class: "ssq-reveal-fix-list" },
        fixRows.map(function (f) {
          return el("li", { class: "ssq-reveal-fix-row" }, [
            el("div", { class: "ssq-reveal-fix-pain" }, [
              el("span", { class: "ssq-reveal-fix-you" }, "YOU SAID"),
              " " + f.pain
            ]),
            el("div", { class: "ssq-reveal-fix-fix" }, [
              el("span", { class: "ssq-reveal-fix-we" }, "UNHINGE"),
              " " + f.fix
            ])
          ]);
        })
      )
    ]) : null;

    // Offer-anchored CTA block: price strip + badges wrap around the button so
    // the click carries the actual offer weight, not just a bare nudge.
    var offer = state.config.revealOffer || {};
    var ctaBlock = el("div", { class: "ssq-reveal-cta-block" }, [
      offer.image ? el("img", {
        class: "ssq-reveal-offer-image",
        src: offer.image,
        alt: offer.imageAlt || offer.product || "UNHINGE",
        loading: "eager"
      }) : null,
      offer.product ? el("div", { class: "ssq-reveal-offer-product" }, [
        el("span", { class: "ssq-reveal-offer-name" }, offer.product),
        offer.servesLine ? el("span", { class: "ssq-reveal-offer-serves" }, offer.servesLine) : null
      ]) : null,
      (offer.current || offer.compareAt) ? el("div", { class: "ssq-reveal-offer-price" }, [
        offer.compareAt ? el("span", { class: "ssq-reveal-offer-strike" }, offer.compareAt) : null,
        offer.current ? el("span", { class: "ssq-reveal-offer-current" }, offer.current) : null
      ]) : null,
      offer.priceNote ? el("p", { class: "ssq-reveal-offer-note" }, offer.priceNote) : null,
      link,
      (offer.badges && offer.badges.length) ? el("ul", { class: "ssq-reveal-offer-badges" },
        offer.badges.map(function (b) {
          return el("li", { class: "ssq-reveal-offer-badge" }, [
            el("span", { class: "ssq-reveal-offer-check", "aria-hidden": "true" }, "✓"),
            " " + b
          ]);
        })
      ) : null
    ]);

    return el("section", { class: "ssq-card ssq-reveal" }, [
      el("div", { class: "ssq-result-tag" }, label),
      el("h2", { class: "ssq-reveal-headline" }, reveal.headline || "Here's what's actually going on with you"),
      el("p", { class: "ssq-reveal-diagnosis" }, diagnosis),
      fixNode,
      ctaBlock,
      reveal.urgency ? el("p", { class: "ssq-reveal-urgency" }, reveal.urgency) : null,
      timelineNode,
      reveal.subline
        ? el("p", { class: "ssq-reveal-subline" }, reveal.subline)
        : el("p", { class: "ssq-reveal-subline" }, "We've prepped your protocol page with the flavour you picked. One click to checkout from there.")
    ]);
  }

  function renderOffer(state, screen) {
    var scores = scoreAnswers(state.config, state.answers);
    var key = resolvePersona(state.config, scores);
    state.persona = key;
    var persona = state.config.personas[key] || {};
    var offer = screen.offer || persona.offer || {};
    if (!state.firedCompletion) fireCompletion(state);

    var price = offer.price || {};
    var bullets = (offer.stackedValue || []).map(function (b) {
      return el("li", { class: "ssq-offer-bullet" }, [
        el("span", { class: "ssq-offer-bullet-label" }, b.label),
        b.value ? el("span", { class: "ssq-offer-bullet-value" }, b.value) : null
      ]);
    });

    var variants = offer.variants || [];
    var defaultVariant = variants.find(function (v) { return v.default; }) || variants[0];
    if (!state.selectedVariantId && defaultVariant) state.selectedVariantId = defaultVariant.id;
    var selectedVariant = variants.find(function (v) { return String(v.id) === String(state.selectedVariantId); }) || defaultVariant;

    var heroImg = (selectedVariant && selectedVariant.image) || offer.product && offer.product.image || null;
    var hero = heroImg
      ? el("img", { class: "ssq-offer-hero", src: heroImg, alt: (offer.product && offer.product.name) || "UNHINGE", loading: "eager" })
      : null;

    var ctaTemplate = offer.cartUrl || (persona.cta && persona.cta.url) || "#";
    var ctaUrl = withUtm(buildCartUrl(ctaTemplate, state.selectedVariantId), state.config.slug, key);

    var flavourPicker = renderFlavourPicker(state, offer, function () { state.render(); });

    return el("section", { class: "ssq-card ssq-offer" }, [
      persona.label ? el("div", { class: "ssq-result-tag" }, persona.label) : null,
      el("h2", { class: "ssq-offer-headline" }, offer.headline || persona.headline || "Here's your protocol"),
      offer.body ? el("p", { class: "ssq-offer-body" }, offer.body) : (persona.body ? el("p", { class: "ssq-offer-body" }, persona.body) : null),
      hero,
      flavourPicker,
      renderBenefits(offer.benefits),
      bullets.length ? el("ul", { class: "ssq-offer-stack" }, bullets) : null,
      el("div", { class: "ssq-price-block" }, [
        price.compareAt ? el("span", { class: "ssq-price-strike" }, price.compareAt) : null,
        el("span", { class: "ssq-price-current" }, price.current || ""),
        price.note ? el("span", { class: "ssq-price-note" }, price.note) : null
      ]),
      (function () {
        var link = el("a", {
          class: "ssq-next ssq-offer-cta",
          href: ctaUrl,
          target: "_self"
        }, offer.ctaLabel || "Start my protocol \u2192");
        link.addEventListener("click", function () {
          // Fire just before navigation; keepalive ensures Klaviyo POST survives unload.
          trackEvent(state, "Quiz CTA Clicked", {
            variant_id: state.selectedVariantId,
            variant_label: (variants.find(function (v) { return String(v.id) === String(state.selectedVariantId); }) || {}).label,
            href: ctaUrl,
            price_current: price.current || null,
            price_compare_at: price.compareAt || null
          }, { keepalive: true });
        });
        return link;
      })(),
      offer.guarantee ? el("p", { class: "ssq-guarantee" }, offer.guarantee) : null,
      renderTestimonial(offer.testimonial)
    ]);
  }

  function renderEmailCapture(state, screen) {
    var form = el("form", { class: "ssq-email-form", novalidate: "" }, [
      el("h2", { class: "ssq-q-prompt" }, screen.headline || "Where should we send your result?"),
      el("input", {
        type: "email", class: "ssq-email-input", name: "email",
        required: "", placeholder: "you@example.com", autocomplete: "email"
      }),
      el("button", { type: "submit", class: "ssq-next ssq-email-submit" }, screen.ctaLabel || "Show my result \u2192"),
      screen.consentLine ? el("p", { class: "ssq-consent" }, screen.consentLine) : null
    ]);
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      var email = form.querySelector(".ssq-email-input").value.trim();
      if (!email || email.indexOf("@") < 1) return;
      state.email = email;
      state.persona = resolvePersona(state.config, scoreAnswers(state.config, state.answers));
      state.persist();
      klaviyoSubscribe(state.config, email);
      trackEvent(state, "Email Captured", { email_domain: email.split("@")[1] || null });
      fireCompletion(state);
      advance(state);
    });
    return el("section", { class: "ssq-card" }, [form]);
  }

  function fireCompletion(state) {
    if (state.firedCompletion) return;
    state.firedCompletion = true;
    var scores = scoreAnswers(state.config, state.answers);
    if (!state.persona) state.persona = resolvePersona(state.config, scores);
    trackEvent(state, "Quiz Completed", {
      answers: state.answers,
      scores: scores,
      selected_variant_id: state.selectedVariantId || null
    });
    state.persist();
  }

  // ────────────────────────────────────────────────────────────────
  function render(state) {
    var root = state.root;
    if (!root) return;
    var screens = visibleScreens(state.config, state.answers);
    if (!screens.length) { root.textContent = "Quiz has no screens configured."; return; }
    if (state.index >= screens.length) state.index = screens.length - 1;
    state.totalScreens = screens.length;

    var screen = screens[state.index];

    // ── Tracking: Quiz Started + Step Viewed (debounced per-step) ──────
    var stepKey = state.index + ":" + (screen.id || screen.type);
    if (!state.firedStarted && (screen.type === "intro" || state.index === 0)) {
      state.firedStarted = true;
      trackEvent(state, "Quiz Started", { first_screen_id: screen.id || null });
    }
    if (!state.firedSteps) state.firedSteps = {};
    if (!state.firedSteps[stepKey]) {
      state.firedSteps[stepKey] = Date.now();
      var prevKey = state._lastStepKey;
      var timeOnPrev = prevKey && state.firedSteps[prevKey] ? Date.now() - state.firedSteps[prevKey] : null;
      trackEvent(state, "Quiz Step Viewed", {
        screen_id: screen.id || null,
        screen_type: screen.type,
        time_on_previous_ms: timeOnPrev
      });
      state._lastStepKey = stepKey;
      if (screen.type === "interstitial") {
        trackEvent(state, "Quiz Interstitial Viewed", { interstitial_id: screen.id || null });
        setTimeout(function () { fireConfetti(state.root); }, 120);
        haptic([10, 40, 10]);
      }
      if (screen.type === "analyzing") {
        trackEvent(state, "Quiz Analyzing Viewed", { duration_ms: screen.duration || 3000 });
        haptic([5, 20, 5]);
      }
      if (screen.type === "offer" || screen.type === "result_reveal") {
        trackEvent(state, "Quiz Offer Viewed", {
          selected_variant_id: state.selectedVariantId || null
        });
        setTimeout(function () { fireConfetti(state.root); }, 400);
        haptic([20, 60, 20, 60, 40]);
      }
    }

    root.innerHTML = "";
    var wrap = el("div", { class: "ssq-wrap" }, [renderProgress(state, screens)]);

    var body;
    if (screen.type === "intro" || (state.index === 0 && screen.type === "question")) {
      body = renderQuestionBody(state, screen, true);
    } else if (screen.type === "question") {
      body = renderQuestionBody(state, screen, false);
    } else if (screen.type === "interstitial") {
      body = renderInterstitial(state, screen);
    } else if (screen.type === "analyzing") {
      body = renderAnalyzing(state, screen);
    } else if (screen.type === "email") {
      body = renderEmailCapture(state, screen);
    } else if (screen.type === "offer") {
      body = renderOffer(state, screen);
    } else if (screen.type === "result_reveal") {
      body = renderResultReveal(state, screen);
    } else {
      body = el("div", {}, "Unknown screen type: " + screen.type);
    }
    wrap.appendChild(body);
    root.appendChild(wrap);
    // Scroll to top of quiz root on each screen change (mobile ergonomics).
    try { root.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (e) {}
  }

  function init(config) {
    if (!config || !config.slug || !Array.isArray(config.screens)) {
      throw new Error("[SSQ] config.slug and config.screens[] required");
    }
    var root = document.getElementById("ssq-root");
    if (!root) {
      root = el("div", { id: "ssq-root" });
      document.body.appendChild(root);
    }
    var storage = makeStorage(config.slug);
    var saved = storage.get();
    var state = {
      config: config, root: root, storage: storage,
      index: (saved && saved.index) || 0,
      answers: (saved && saved.answers) || {},
      email: saved && saved.email,
      persona: saved && saved.persona,
      sessionId: (saved && saved.sessionId) || uuid(),
      firedCompletion: saved && saved.firedCompletion,
      selectedVariantId: saved && saved.selectedVariantId,
      firedStarted: saved && saved.firedStarted,
      firedSteps: (saved && saved.firedSteps) || {},
      persist: function () {
        storage.set({
          index: state.index, answers: state.answers,
          email: state.email, persona: state.persona,
          sessionId: state.sessionId, firedCompletion: state.firedCompletion,
          selectedVariantId: state.selectedVariantId,
          firedStarted: state.firedStarted, firedSteps: state.firedSteps
        });
      },
      render: function () { render(state); }
    };
    state.render();
    return state;
  }

  global.SSQuiz = { init: init };

  if (typeof document !== "undefined") {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", function () {
        if (global.SSQ_CONFIG) init(global.SSQ_CONFIG);
      });
    } else if (global.SSQ_CONFIG) {
      init(global.SSQ_CONFIG);
    }
  }
})(typeof window !== "undefined" ? window : this);
